/* eslint-disable no-nested-ternary */
import { formatToCommaSeparatedString } from "utils/HelperFunctions";
import { CloseIcon, RefreshIcon, BrightBoxIcon } from "components/atoms/Icon";
import { useMemo } from "react";
import useCountdown from "components/hooks/useCountDown";
import { DetailHeadingTemplate } from "components/atoms/DetailHeading";
import moment from "moment";
import Button from "components/atoms/buttons/Button";
import { clsx } from "clsx";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";
import HintIconWithTooltip from "components/molecules/forms/HintIconWithTooltip";

function MagnetCountDownTimer({
  startTime,
  refetchMagnetDetails,
  isMagnetDetailsRefetching,
  textDark = false,
}: {
  textDark?: boolean;
  startTime: number;
  refetchMagnetDetails?: () => void;
  isMagnetDetailsRefetching?: boolean;
}) {
  const { days, hours, minutes, seconds, saleTimePastCurrentTime } = useCountdown(startTime);

  return (
    <div className="flex flex-col font-bold">
      <div className="mt-1 flex  flex-row items-center justify-center space-x-1">
        <p className="  font-satoshi text-base font-medium text-[#7E7A7A] lg:text-center ">Sale starts in</p>
        {refetchMagnetDetails && saleTimePastCurrentTime && (
          <span role="button" onKeyDown={() => {}} tabIndex={0} onClick={refetchMagnetDetails}>
            <RefreshIcon className={clsx("h-2 w-2 md:h-3 md:w-3", isMagnetDetailsRefetching && "animate-spin")} />
          </span>
        )}
      </div>

      <div
        className={`mt-1 flex items-center justify-start space-x-2 px-1  font-bold md:space-x-4 ${
          textDark ? "[&_div]:text-gray-700" : ""
        }`}
      >
        <DetailHeadingTemplate heading={days} subHeading="Days" />
        <DetailHeadingTemplate heading={hours} subHeading="Hours" />
        <DetailHeadingTemplate heading={minutes} subHeading="Mins" />
        <DetailHeadingTemplate heading={seconds} subHeading="Secs" />
      </div>
    </div>
  );
}

function MagnetPriceAndSupply({
  price,
  maxSupply,
  remainingSupply,
  hidePrice,
  textDark = false,
}: {
  price: number;
  maxSupply: number;
  remainingSupply: number;
  hidePrice?: boolean;
  textDark?: boolean;
}) {
  return (
    <div className="flex items-center justify-center gap-x-6 text-center text-base  md:text-xs">
      {!hidePrice && (
        <div className="">
          <p className={`font-bold ${textDark ? "text-gray-800" : "text-[#7E7A7A]"}`}>Price</p>
          <p className={`font-semibold ${textDark ? "text-gray-700" : "text-white"}`}>
            {formatToCommaSeparatedString(price, "currency")}
          </p>
        </div>
      )}
      <div className="">
        <p className={`font-bold ${textDark ? "text-gray-800" : "text-[#7E7A7A]"}`}>Supply</p>

        <p className={`font-semibold ${textDark ? "text-gray-700" : "text-white"}`}>
          {/* @ts-ignore */}
          {`${formatToCommaSeparatedString(remainingSupply, "decimal")}/${formatToCommaSeparatedString(
            maxSupply,
            "decimal",
          )}`}
        </p>
      </div>
    </div>
  );
}

type Props = {
  release: {} | Release;
  magnetName: string;
  variant?: 0 | 1;
  isInteractionBlocked: boolean;
  magnetType: MagnetProgramTypes;
  handleEligibilityCheckClick?: () => void;
  isCustomizedUIBrand?: boolean;
};

interface MagnetPagePriceInfoVariant extends Props {
  isMagnetPageVariant: true;
  isMagnetOwnedByUser: boolean;
  handleMagnetBuy: () => void;
  restrictMagnetClaim: boolean;
  refetchMagnetDetails: () => void;
  isMagnetDetailsRefetching: boolean;
  handleClaimMagnet: () => void;
}

interface BrandPagePriceInfoVariant extends Props {
  isMagnetPageVariant?: never;
  isMagnetOwnedByUser?: never;
  handleMagnetBuy?: never;
  restrictMagnetClaim?: never;
  refetchMagnetDetails?: never;
  isMagnetDetailsRefetching?: never;
  handleClaimMagnet?: never;
}

function MagnetPriceInfoWidget({
  isCustomizedUIBrand = false,
  release,
  magnetName,
  variant = 0,
  isInteractionBlocked,
  handleMagnetBuy,
  handleClaimMagnet,
  restrictMagnetClaim,
  isMagnetOwnedByUser,
  refetchMagnetDetails,
  isMagnetDetailsRefetching,
  isMagnetPageVariant,
  magnetType = 0,
  handleEligibilityCheckClick,
}: BrandPagePriceInfoVariant | MagnetPagePriceInfoVariant) {
  const magnetPriceInfo = useMemo(() => {
    if (isMagnetOwnedByUser) {
      return (
        <div className="flex  flex-col items-center justify-center rounded-md bg-opacity-5 py-2 text-center lg:h-full lg:rounded-xl">
          <p
            className={`flex justify-center rounded-full border-1 ${
              isCustomizedUIBrand ? "border-[#842572]" : "border-lime-400"
            } py-2 pl-3 pr-4 pt-3 font-satoshi text-base font-bold uppercase tracking-wider ${
              isCustomizedUIBrand ? "text-[#842572]" : "text-lime-400"
            } underline md:py-3 md:pl-5 md:pr-6 md:pt-4`}
          >
            <BrightBoxIcon
              color={`${isCustomizedUIBrand ? "text-[#842572]" : "text-lime-400"}`}
              className="my-auto -mt-1 mr-2 inline-block h-6 w-6 md:h-7 md:w-7"
            />
            <span>You own this Magnet</span>
          </p>
        </div>
      );
    }
    // @ts-ignore
    if (release?.event?.event_id)
      return (
        <div className="flex  flex-col items-center justify-center rounded-md bg-opacity-5 py-2 text-center lg:h-full lg:rounded-xl">
          <p className="flex items-center justify-center rounded-full border-1 border-gray-700 py-2 pl-3 pr-4 font-satoshi text-base tracking-wider text-gray-700 md:px-6 md:py-3">
            <span className="font-bold">CLAIM VIA PRIVATE URL</span>
            <span>
              <InformationCircleIcon className="event-claim ml-2 inline-block h-5 w-5 cursor-pointer text-gray-700" />
              <Tooltip
                anchorSelect=".event-claim"
                variant="dark"
                place="bottom"
                style={{ opacity: 1, background: "#1f2732" }}
              >
                <p className="w-72 text-left text-xxs font-medium">
                  This Magnet is presently under a private release i.e. it can only be acquired through the brand&apos;s
                  private event URL.
                </p>
              </Tooltip>
            </span>
          </p>
        </div>
      );
    if ("status" in release) {
      switch (release.status) {
        case 2:
          return (
            <div className="flex h-full flex-col items-start justify-between text-center md:justify-around lg:mx-auto lg:items-center lg:px-4">
              <MagnetPriceAndSupply
                remainingSupply={release.max_supply - (release?.sold || 0)}
                price={release.price}
                maxSupply={release.max_supply}
                textDark={isCustomizedUIBrand}
              />
              <MagnetCountDownTimer
                startTime={release.start_time}
                isMagnetDetailsRefetching={isMagnetDetailsRefetching}
                refetchMagnetDetails={refetchMagnetDetails}
                textDark
              />
            </div>
          );
        // On Sale
        case 3:
          return (
            <div
              className={clsx(
                isMagnetPageVariant ? "gap-y-3" : "gap-y-1",
                "flex h-full  min-h-[90px] flex-col items-start justify-around  text-center  lg:items-center lg:px-4",
              )}
            >
              <MagnetPriceAndSupply
                remainingSupply={release.max_supply - (release?.sold || 0)}
                price={release.price}
                maxSupply={release.max_supply}
                hidePrice={release.price === 0}
                textDark={isCustomizedUIBrand}
              />
              {isMagnetPageVariant ? (
                <div className=" w-full">
                  <Button
                    text={
                      // Sold out
                      // eslint-disable-next-line no-nested-ternary
                      release.max_supply - (release.sold || 0)
                        ? // Zero price
                          release.price === 0
                          ? isCustomizedUIBrand
                            ? "ENROLL NOW"
                            : "CLAIM NOW"
                          : "BUY NOW"
                        : "SOLD OUT"
                    }
                    disabled={!(release.max_supply - (release.sold || 0))}
                    onClick={release.price === 0 ? handleClaimMagnet : handleMagnetBuy}
                    variant="magnetiqSolid"
                    additionalClasses={`lg:!mx-auto !text-[10px] lg:!text-[16px] !py-1 lg:!py-3 lg:!px-5 ${
                      isCustomizedUIBrand ? "!bg-[#842572] !text-white" : "bg-accent"
                    }`}
                  />
                  {magnetType === 1 && (
                    <button
                      className={`mt-3 text-base ${
                        isCustomizedUIBrand ? "!text-[#842572]" : "!text-accent"
                      } underline underline-offset-4`}
                      type="button"
                      onClick={handleEligibilityCheckClick}
                    >
                      Members Only Magnet - Check Eligibility
                    </button>
                  )}
                </div>
              ) : (
                <Button
                  text="View Magnet"
                  variant="whiteOutline"
                  additionalClasses="whitespace-nowrap !text-base hidden lg:block "
                  onClick={() => {}}
                  type="button"
                />
              )}
              <div className={clsx(isMagnetPageVariant && "hidden md:flex", "flex flex-col items-center space-y-3")}>
                <div className="flex items-center gap-x-1">
                  <p className="text-base text-[#16B8A2]">
                    {isMagnetPageVariant ? "" : "Sale is started buy now"}
                    {release.is_whitelisted ? (
                      <span className="inline-flex items-center">
                        (Exclusive)
                        <HintIconWithTooltip
                          hintText="Exclusive for members whitelisted by brand"
                          direction="bottom"
                          elementClassName="scale-90"
                        />
                      </span>
                    ) : (
                      ""
                    )}
                  </p>
                </div>
              </div>
            </div>
          );
        // Sale Ended
        case 4:
          return (
            <div className="flex h-full items-center justify-center rounded-md bg-white bg-opacity-5 px-5 text-center lg:flex-col lg:rounded-xl ">
              <CloseIcon className="hidden h-4 w-4 md:block " />

              <p className=" py-3 text-base lg:mt-2 lg:text-xs ">{`Sale ended on ${moment(release.end_time).format(
                "MMMM D, YYYY",
              )}`}</p>
            </div>
          );
        default:
          return null;
      }
    }

    if (isInteractionBlocked) return null;

    return (
      <p className="flex  flex-col items-center justify-center rounded-md bg-white bg-opacity-5 py-2 text-center  text-xxs lg:h-full lg:rounded-xl">
        Coming Soon
      </p>
    );
  }, [release, isMagnetOwnedByUser, handleMagnetBuy, isInteractionBlocked, restrictMagnetClaim, isMagnetPageVariant]);

  return (
    <div
      className={`flex h-full flex-col justify-between  font-bold text-${isCustomizedUIBrand ? "primary" : "white"}`}
    >
      {variant === 0 && <p className="text-xxs  font-bold xxs:text-sm lg:text-lg">{magnetName}</p>}
      <div className="lg:h-full">{magnetPriceInfo}</div>
    </div>
  );
}

export default MagnetPriceInfoWidget;
